import Divided from "components/organisms/common/Divided"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import useTranslations from "hooks/useTranslations"
import { ThreatsImage } from "./images"

/**
 * The harms list from the UK sales deck, with slide 3's photograph and its two
 * proof figures.
 *
 * Kaisa: "a bit text heavy, could you throw a pic here?" — so it runs through
 * Divided with an image rather than as a full-width block of prose, and then
 * "should have an image from slide 3, also needs numbers from slide 3".
 *
 * The harms stay chips rather than a bulleted column: eight short nouns read
 * as a landscape at a glance, which is the point of the section, and it
 * matches how the deck lays them out.
 *
 * Both figures carry the deck's own footnote. The 30% is a Swedish student
 * survey, not a UK one, and saying so is the difference between a claim a head
 * teacher can check and one they can't.
 */
export default function UkThreats() {
  const {
    ukSchoolsThreatsTitle,
    ukSchoolsThreatsLead,
    ukSchoolsThreatsItems,
    ukSchoolsThreatsClosing,
    ukSchoolsThreatsStatValues,
    ukSchoolsThreatsStatLabels,
    ukSchoolsThreatsStatNote,
  } = useTranslations()

  const statValues = ukSchoolsThreatsStatValues || []
  const statLabels = ukSchoolsThreatsStatLabels || []

  return (
    <Divided
      Image={ThreatsImage}
      title={ukSchoolsThreatsTitle}
      theme="light"
      titleSize="lg"
    >
      <div className="space-y-8">
        {(ukSchoolsThreatsLead || []).map((paragraph) => (
          <p
            key={paragraph}
            className="m-0 font-display text-lg leading-tight text-zinc-700 lg:text-xl"
          >
            {paragraph}
          </p>
        ))}

        <ul className="m-0 flex list-none flex-wrap gap-2.5 p-0">
          {(ukSchoolsThreatsItems || []).map((item, index) => (
            <ScrollAnimate key={item} delay={index * 0.05}>
              <li className="rounded-full border-2 border-kkm-lavender bg-kkm-lavender/25 px-4 py-1.5 font-display text-sm text-zinc-900 lg:text-base">
                {item}
              </li>
            </ScrollAnimate>
          ))}
        </ul>

        <p className="m-0 font-display text-lg leading-tight text-zinc-800 lg:text-xl">
          {ukSchoolsThreatsClosing}
        </p>

        {statValues.length ? (
          <div className="space-y-3 border-t border-zinc-200 pt-8">
            <dl className="m-0 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              {statValues.map((value, index) => (
                <ScrollAnimate key={value} delay={index * 0.1}>
                  <div>
                    {/* The figure is the headline here, so it is marked up as
                        the term and the sentence as its definition — which is
                        also the order a screen reader reads them in. */}
                    <dt className="m-0 font-display text-5xl font-extrabold leading-none tracking-tight text-teal-700 lg:text-6xl">
                      {value}
                    </dt>
                    <dd className="m-0 mt-2 text-base leading-snug text-zinc-700">
                      {statLabels[index] || ""}
                    </dd>
                  </div>
                </ScrollAnimate>
              ))}
            </dl>
            {ukSchoolsThreatsStatNote ? (
              <p className="m-0 pt-2 text-sm text-zinc-500">
                {ukSchoolsThreatsStatNote}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Divided>
  )
}
